import React, { useMemo } from 'react';
import { UserX, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import type { DemandaEsteira } from '../../../types';
import { resumoGerencial, bloqueadoresInternos } from './normalizar';

interface Props {
    demandas: DemandaEsteira[];
    /** E-mail corporativo → nome do perfil (ver `bloqueadoresInternos`). */
    nomePorEmail?: Map<string, string>;
    /** Clicar numa pessoa filtra a Lista pelos itens dela. */
    onVerPessoa: (responsavelId: string | null) => void;
}

/**
 * Modo Resumo — para quem não trabalha fila, mas precisa saber se o time dá conta.
 *
 * A gerente exportou planilha de faturas 17 vezes em 12 dias: a pergunta dela
 * ("quem está com o quê, e há quanto tempo?") era respondida fora do sistema.
 * Aqui ela vem pronta, sobre o mesmo recorte filtrado dos outros modos.
 *
 * Tudo sai de `resumoGerencial` e `bloqueadoresInternos` (normalizar.ts), então
 * os números batem com a Lista e com o Quadro — não há conta paralela.
 */
const ResumoGerencial: React.FC<Props> = ({ demandas, nomePorEmail, onVerPessoa }) => {
    const { porPessoa, porFaixa, totalPendente } = useMemo(
        () => resumoGerencial(demandas, new Date()),
        [demandas]
    );
    const bloqueadores = useMemo(() => bloqueadoresInternos(demandas, nomePorEmail), [demandas, nomePorEmail]);

    const maiorFaixa = Math.max(1, ...porFaixa.map(f => f.total));
    const maiorPessoa = Math.max(1, ...porPessoa.map(p => p.total));
    const semDono = porPessoa.find(p => p.responsavelId === null)?.total ?? 0;
    const velhas = porFaixa.slice(3).reduce((s, f) => s + f.total, 0);

    return (
        <div className="space-y-4">
            {/* Frase de abertura: a resposta em uma linha, antes dos gráficos. */}
            <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                    <strong className="text-gray-900 dark:text-white">{totalPendente}</strong> demandas pendentes neste recorte.{' '}
                    {velhas ? <><strong className="text-red-600 dark:text-red-400">{velhas}</strong> estão paradas há mais de 30 dias. </> : null}
                    {semDono ? <><strong className="text-amber-600 dark:text-amber-400">{semDono}</strong> não têm dono. </> : null}
                    {bloqueadores.length
                        ? <>A assinatura interna mais pendente é de <strong>{bloqueadores[0].nome}</strong> ({bloqueadores[0].demandas}).</>
                        : null}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Idade — o termômetro do atraso. */}
                <section className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                        <Clock size={15} className="text-gray-400" /> Há quanto tempo está parado
                    </h3>
                    <ul className="space-y-2.5">
                        {porFaixa.map((f, i) => (
                            <li key={f.rotulo}>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-gray-600 dark:text-gray-300">{f.rotulo}</span>
                                    <span className="font-bold text-gray-900 dark:text-white">{f.total}</span>
                                </div>
                                <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                    <div
                                        // A cor esquenta com a idade: o fim da escala é o problema.
                                        className={`h-full rounded-full ${
                                            i >= 3 ? 'bg-red-500' : i === 2 ? 'bg-amber-500' : 'bg-primary/70'
                                        }`}
                                        style={{ width: `${Math.round((f.total / maiorFaixa) * 100)}%` }}
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>

                {/* Bloqueio interno — só aparece se existir. */}
                <section className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-1">
                        <AlertTriangle size={15} className="text-orange-500" /> Esperando assinatura da Vigor
                    </h3>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-3">
                        Documentos travados por gente de casa — não dependem do cliente.
                    </p>
                    {bloqueadores.length === 0 ? (
                        <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">Ninguém da Vigor segurando assinatura.</p>
                    ) : (
                        <ul className="space-y-2">
                            {bloqueadores.map(b => (
                                <li key={b.email} className="flex items-center justify-between gap-3 text-xs">
                                    <span className="text-gray-700 dark:text-gray-200 truncate">{b.nome}</span>
                                    <span className="font-black text-orange-600 dark:text-orange-400 shrink-0">{b.demandas}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {/* Por pessoa — a carga de cada um, clicável para ver os itens. */}
            <section className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Carga por pessoa</h3>
                {porPessoa.length === 0 ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">Nada neste recorte.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-gray-400 dark:text-gray-500 text-left">
                                    <th className="font-semibold pb-2 pr-3">Pessoa</th>
                                    <th className="font-semibold pb-2 pr-3 w-1/3">Pendentes</th>
                                    <th className="font-semibold pb-2 pr-3 text-right">+7 dias</th>
                                    <th className="font-semibold pb-2 pr-3 text-right">Mais antiga</th>
                                    <th className="font-semibold pb-2 text-right">Hoje</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                                {porPessoa.map(p => {
                                    const semDonoLinha = p.responsavelId === null;
                                    return (
                                        <tr key={p.responsavelId ?? 'sem-dono'}>
                                            <td className="py-2 pr-3">
                                                <button
                                                    onClick={() => onVerPessoa(p.responsavelId)}
                                                    className={`font-semibold hover:underline flex items-center gap-1.5 ${
                                                        semDonoLinha ? 'text-amber-600 dark:text-amber-400' : 'text-gray-800 dark:text-gray-100 hover:text-primary'
                                                    }`}
                                                    title={semDonoLinha ? 'Ver o que ninguém assumiu' : `Ver os itens de ${p.nome}`}
                                                >
                                                    {semDonoLinha ? <UserX size={13} /> : null}
                                                    {p.nome}
                                                </button>
                                            </td>
                                            <td className="py-2 pr-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${semDonoLinha ? 'bg-amber-500' : 'bg-primary/70'}`}
                                                            style={{ width: `${Math.round((p.total / maiorPessoa) * 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className="font-bold text-gray-900 dark:text-white w-10 text-right">{p.total}</span>
                                                </div>
                                            </td>
                                            <td className="py-2 pr-3 text-right font-semibold text-red-600 dark:text-red-400">
                                                {p.paradasMais7 || '—'}
                                            </td>
                                            <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">
                                                {p.maisAntiga ? `${p.maisAntiga}d` : '—'}
                                            </td>
                                            <td className="py-2 text-right">
                                                {p.trabalhadasHoje ? (
                                                    <span className="inline-flex items-center gap-1 font-bold text-emerald-600 dark:text-emerald-400">
                                                        <CheckCircle2 size={12} /> {p.trabalhadasHoje}
                                                    </span>
                                                ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default ResumoGerencial;
